"use strict";
import { Token } from "@uniswap/sdk-core";
import { SqrtPriceMath, TickMath, tickToPrice } from "@uniswap/v3-sdk";
import JSBI from "jsbi";
import {
  CL_APR_REFERENCE_DEPOSIT_USD,
  MIN_STAKED_TVL_USD,
  SECONDS_PER_YEAR,
  calculateAprPercent,
  getPresetSpreadPercent,
  getTicksForSpread,
  isRewardPeriodActive
} from "services/cl-apr";
import { formatUnits } from "viem";
const REFERENCE_LIQUIDITY = JSBI.BigInt("1000000000000000000");
export const MAX_GAUGE_APR_PERCENT = 1e4;
export function computeV3GaugeApr({
  pool,
  rewardRatePerSecond,
  stakedLiquidity,
  poolLiquidityRaw,
  stakedTvlUsd,
  topazPriceUsd,
  chainId,
  rewardPeriodFinish,
  currentTimeSeconds
}) {
  if (rewardRatePerSecond === void 0 || rewardRatePerSecond <= 0) {
    return {};
  }
  if (!topazPriceUsd || topazPriceUsd <= 0) {
    return {};
  }
  if (rewardPeriodFinish !== void 0) {
    const nowSeconds = currentTimeSeconds ?? Math.floor(Date.now() / 1e3);
    if (!isRewardPeriodActive(rewardPeriodFinish, nowSeconds)) {
      return {};
    }
  }
  if (stakedTvlUsd === void 0 || stakedTvlUsd <= 0 || stakedTvlUsd < MIN_STAKED_TVL_USD || !Number.isFinite(stakedTvlUsd)) {
    return {};
  }
  const baseAprFraction = rewardRatePerSecond * SECONDS_PER_YEAR * topazPriceUsd / stakedTvlUsd;
  if (stakedLiquidity === void 0 || poolLiquidityRaw === void 0) {
    return { baseAprFraction };
  }
  try {
    const token0Info = pool.token0;
    const token1Info = pool.token1;
    const token0 = new Token(
      chainId,
      token0Info.id,
      Number(token0Info.decimals ?? "18"),
      token0Info.symbol,
      token0Info.name
    );
    const token1 = new Token(
      chainId,
      token1Info.id,
      Number(token1Info.decimals ?? "18"),
      token1Info.symbol,
      token1Info.name
    );
    const currentTick = Number(pool.tick || "0");
    const tickSpacingNumber = pool.tickSpacingNumber;
    if (!tickSpacingNumber || tickSpacingNumber <= 0) {
      return { baseAprFraction };
    }
    const sqrtPriceX96 = JSBI.BigInt(pool.sqrtPrice || "0");
    if (JSBI.equal(sqrtPriceX96, JSBI.BigInt(0))) {
      return { baseAprFraction };
    }
    const spreadPercent = getPresetSpreadPercent(token0Info.symbol, token1Info.symbol, tickSpacingNumber);
    const { tickLower: lowerTick, tickUpper: upperTick } = getTicksForSpread(
      currentTick,
      spreadPercent,
      tickSpacingNumber
    );
    if (lowerTick >= upperTick) {
      return { baseAprFraction };
    }
    const amount0Locked = Number(pool.totalValueLockedToken0 || "0");
    const amount1Locked = Number(pool.totalValueLockedToken1 || "0");
    const totalUsdLocked = Number(pool.totalValueLockedUSD || "0");
    const priceRatio = parseFloat(tickToPrice(token0, token1, currentTick).toSignificant(18));
    const usdDenominator = amount0Locked * priceRatio + amount1Locked;
    if (!(usdDenominator > 0) || !(totalUsdLocked > 0) || !Number.isFinite(priceRatio) || priceRatio <= 0) {
      return { baseAprFraction };
    }
    const price1Usd = totalUsdLocked / usdDenominator;
    const price0Usd = price1Usd * priceRatio;
    const { amount0: refAmount0, amount1: refAmount1 } = getAmountsForLiquidity({
      sqrtPriceX96,
      tickLower: lowerTick,
      tickUpper: upperTick,
      currentTick,
      liquidity: REFERENCE_LIQUIDITY
    });
    const referenceValueUsd = calculatePositionValueUsd({
      amount0: refAmount0,
      amount1: refAmount1,
      token0Decimals: token0.decimals,
      token1Decimals: token1.decimals,
      price0Usd,
      price1Usd
    });
    if (!Number.isFinite(referenceValueUsd) || referenceValueUsd <= 0) {
      return { baseAprFraction };
    }
    const scaledLiquidityNumber = Math.floor(
      JSBI.toNumber(REFERENCE_LIQUIDITY) * (CL_APR_REFERENCE_DEPOSIT_USD / referenceValueUsd)
    );
    if (!Number.isFinite(scaledLiquidityNumber) || scaledLiquidityNumber <= 0) {
      return { baseAprFraction };
    }
    const positionLiquidity = BigInt(scaledLiquidityNumber);
    const { amount0, amount1 } = getAmountsForLiquidity({
      sqrtPriceX96,
      tickLower: lowerTick,
      tickUpper: upperTick,
      currentTick,
      liquidity: JSBI.BigInt(positionLiquidity.toString())
    });
    const positionValueUsd = calculatePositionValueUsd({
      amount0,
      amount1,
      token0Decimals: token0.decimals,
      token1Decimals: token1.decimals,
      price0Usd,
      price1Usd
    });
    if (!Number.isFinite(positionValueUsd) || positionValueUsd <= 0) {
      return { baseAprFraction };
    }
    const totalStakedAfter = stakedLiquidity + positionLiquidity;
    const apr = calculateAprPercent({
      positionLiquidity,
      stakedLiquidity: totalStakedAfter,
      rewardRatePerSecond,
      rewardTokenPriceUsd: topazPriceUsd,
      positionValueUsd
    });
    if (apr === void 0 || !Number.isFinite(apr) || apr <= 0) {
      return { baseAprFraction };
    }
    const cappedApr = Math.min(apr, MAX_GAUGE_APR_PERCENT);
    return {
      baseAprFraction,
      rangeAprFraction: cappedApr / 100,
      // Convert to fraction for backwards compatibility
      apr: cappedApr
    };
  } catch (error) {
    console.error("[Liquidity] failed to compute V3 APR preset", error);
    return { baseAprFraction };
  }
}
function getAmountsForLiquidity({
  sqrtPriceX96,
  tickLower,
  tickUpper,
  currentTick,
  liquidity
}) {
  const sqrtRatioAX96 = TickMath.getSqrtRatioAtTick(tickLower);
  const sqrtRatioBX96 = TickMath.getSqrtRatioAtTick(tickUpper);
  if (currentTick < tickLower) {
    return {
      amount0: SqrtPriceMath.getAmount0Delta(sqrtRatioAX96, sqrtRatioBX96, liquidity, false),
      amount1: JSBI.BigInt(0)
    };
  }
  if (currentTick < tickUpper) {
    return {
      amount0: SqrtPriceMath.getAmount0Delta(sqrtPriceX96, sqrtRatioBX96, liquidity, false),
      amount1: SqrtPriceMath.getAmount1Delta(sqrtRatioAX96, sqrtPriceX96, liquidity, false)
    };
  }
  return {
    amount0: JSBI.BigInt(0),
    amount1: SqrtPriceMath.getAmount1Delta(sqrtRatioAX96, sqrtRatioBX96, liquidity, false)
  };
}
function calculatePositionValueUsd({
  amount0,
  amount1,
  token0Decimals,
  token1Decimals,
  price0Usd,
  price1Usd
}) {
  const value0 = Number(formatUnits(BigInt(amount0.toString()), token0Decimals)) * price0Usd;
  const value1 = Number(formatUnits(BigInt(amount1.toString()), token1Decimals)) * price1Usd;
  return value0 + value1;
}
